// The NATS wire contracts a box sees — assignment, cancel, mailbox — and the
// cleartext caps an assignment carries.
//
// Here, beside `fleet-box-event` (`fleet.box.<runnerId>`) and `fleet-update`
// (`fleet.update.<runnerId>`), because those are the same fleet plane in the other
// two directions and a wire contract is not part of the task's own state. It sat
// under `entities/task/` while its siblings there answer what a task IS (status,
// failure, hold, outcome); this answers what crosses `fleet.task.<runner_id>`.

import { z } from 'zod';
import { AgentModeAssignmentSchema } from '../entities/agent-mode';
import { RunnerSystemPromptSchema } from '../entities/runner';
import { FleetSystemPromptLayersSchema } from '../entities/system-prompt';
import { OutcomeKindSchema } from '../entities/task/outcome';
import { FleetCoAuthorSchema } from './co-author';
import { CONVERSATION_DIGEST_MAX_CHARS } from './conversation-digest';
import { TaskIdSchema } from './ids';
import { SealedLlmCredentialSchema } from './llm-credential';
import { OpsApprovalSchema } from './ops-approval';
import { SealedVaultEntrySchema, VAULT_MAX_ENTRIES } from './project-ingress';
import { ReasoningEffortSchema } from './reasoning-effort';
import { TaskEnvelopeSchema } from './task-envelope';

/**
 * Ceiling on the KB digest an assignment may carry. Generous relative to what
 * `renderKbDigest` actually emits (40 pages × a ~100-char summary ≈ 6k), because
 * the bound exists to keep a pathological repo off the wire, not to trim a healthy
 * one — the digest's own selection already drops the least-used tail, and
 * `kb_search` recovers it on demand. Matches `SYSTEM_PROMPT_MAX_CHARS` so the two
 * cleartext prose payloads on one assignment are bounded alike.
 */
export const KB_DIGEST_MAX_CHARS = 20_000;

/**
 * Ceiling on the CARRY-OVER an assignment may carry: what the earlier runs of this task
 * left on record (`carry_over` below).
 *
 * A bound on how much of a task's HISTORY a new run is handed, never a token budget
 * (`docs/standards/agent-budgets.md`). The failure it prevents: a task in a retry loop —
 * a failure lane re-queues it, a box releases it five times an hour before
 * `box_release_loop` ends it — accumulates runs without limit, and an unbounded prefix of
 * stale narration would bury the problem statement and the repo evidence the new run has
 * to work from under claims about a tree that has since moved. The renderer keeps the
 * NEWEST runs and cuts the oldest first; this ceiling is its backstop. Sized with
 * `CONVERSATION_DIGEST_MAX_CHARS` (12,000), the other per-task history on the wire.
 */
export const CARRY_OVER_DIGEST_MAX_CHARS = 12_000;

/**
 * Ceiling on the claimed box's display name an assignment may carry (#3878). A
 * generated name (`generateDisplayName`) is two title-cased words, at most 15
 * chars; 64 leaves room for a hand-set operator name (`tesote-hetzner-01 vm-3`)
 * while keeping one footer clause one line. `runners.display_name` itself is an
 * unbounded `text`: the API's rename (`PATCH /v1/runners/:id`, #2268) refuses a
 * name past this, but the column holds whatever was written before or around it,
 * so the assignment enforces this bound by {@link cleanBoxName} truncating, never
 * by the parse refusing.
 */
export const BOX_NAME_MAX_CHARS = 64;

/** Whitespace, C0/C1 controls, and invisible format chars (zero-width, bidi overrides). */
const BOX_NAME_SEPARATORS = /[\s\p{Cc}\p{Cf}]+/gu;

/**
 * The wire shape of a box name: one line, single-spaced, trimmed, bounded — the
 * exact image of {@link cleanBoxName}, so a box renders it with no escaping of
 * its own.
 */
export const BoxNameSchema = z
  .string()
  .min(1)
  .max(BOX_NAME_MAX_CHARS)
  .regex(
    /^[^\s\p{Cc}\p{Cf}]+(?: [^\s\p{Cc}\p{Cf}]+)*$/u,
    'box name must be clean single-line text',
  );

/**
 * Clean a stored `runners.display_name` into a {@link BoxNameSchema} value, or
 * `undefined` when nothing nameable is left. Pure.
 *
 * It exists because the assignment is parsed INSIDE the claim transaction: a
 * hand-set name with a newline or 200 chars would otherwise make every claim for
 * that box throw and roll back, starving the box over a cosmetic field. Every run
 * of whitespace / control / format chars becomes one space, the ends are trimmed,
 * and the result is truncated to {@link BOX_NAME_MAX_CHARS} by code point (never
 * splitting a surrogate pair) and re-trimmed.
 */
export function cleanBoxName(raw: string | null | undefined): string | undefined {
  if (raw == null) return undefined;
  const collapsed = raw.replace(BOX_NAME_SEPARATORS, ' ').trim();
  let out = '';
  for (const ch of collapsed) {
    if (out.length + ch.length > BOX_NAME_MAX_CHARS) break;
    out += ch;
  }
  const bounded = out.trimEnd();
  return bounded.length > 0 ? bounded : undefined;
}

/**
 * The limits the mothership resolved for the assignment's `llm_credential` model
 * (#4240): `modelLimits` (`@developerz/agent`) over the key's DETECTED limits, the
 * registry and the tables — the one answer a box cannot compute itself, because it
 * holds neither the key's `/models` capture nor, on a gateway, a registry-keyable
 * provider. `max_output_tokens: null` = nobody sourced one. `source` is the
 * resolver's (`models_api | overflow_error | registry | table | floor`), kept a
 * string so a new source never makes an old box refuse the whole assignment.
 */
export const FleetModelLimitsSchema = z.object({
  context_window: z.number().int().positive(),
  max_output_tokens: z.number().int().positive().nullable(),
  source: z.string().min(1),
});
export type FleetModelLimits = z.infer<typeof FleetModelLimitsSchema>;

/**
 * A route's reasoning effort as it crosses the wire (plan 101 slice 17): the level AND
 * the model the route pinned it for — the pairing the API validated at write
 * (`apps/api/src/routes/llm-routes.ts` requires a `model` beside any effort).
 *
 * ONE object, not two sibling fields, because the two facts are one decision: the
 * level is only known-good for `route_model`. The box maps the level onto the model it
 * ACTUALLY drives (`reasoningOptions`, `@developerz/agent`) — on a coding lane with no
 * sealed credential that is the box's own model, not the route's — and when that
 * refuses, its error names both models so the reader sees the mismatch instead of a
 * bare refusal.
 *
 * Independent of `llm_credential`: a coding lane run on the box env (no credential
 * sent, `ORIGIN_READS_LLM_CREDENTIAL`, plan 102 slice 07) still gets the route's
 * effort, and it reaches that lane only here.
 */
export const FleetReasoningEffortSchema = z.object({
  level: ReasoningEffortSchema,
  route_model: z.string().min(1),
});
export type FleetReasoningEffort = z.infer<typeof FleetReasoningEffortSchema>;

/**
 * Wire contract for `fleet.task.<runner_id>` — what the assigner publishes and
 * the runner pull-consumer decodes (#121). Deliberately minimal: `task_id` keys
 * the runner's status transitions + the `fleet.evt.<run_id>` stream it streams
 * back on, and `envelope` is the work to execute (it already carries
 * `account_id`, repo, problem, acceptance criteria). No `run_id` (minted when
 * the runner starts). snake_case: it crosses NATS verbatim, same convention as
 * `TaskEnvelope`. Lives here — not in `@developerz/nats` (pure transport) —
 * because both the mothership worker and the runner depend on domain, neither on
 * the other.
 *
 * `llm_credential` (arc 101 slice 04, optional): the pool key the assigner
 * resolved for THIS task, shipped SEALED — `sealed_key` is KEK-wrapped
 * ciphertext, decrypted only on the box, never plaintext at rest or in an audit
 * line (`llmCredentialAuditFields`). The `TaskEnvelope` itself still carries no key
 * material — only the non-secret `llm` preference; the secret rides here, on the
 * ephemeral per-runner assignment, so "the box never picks its own creds".
 * Absent → the box falls back to its own `DZ_BACKEND_*` env (pre-pool behavior).
 */
export const FleetTaskAssignmentSchema = z.object({
  task_id: TaskIdSchema,
  envelope: TaskEnvelopeSchema,
  /**
   * The FENCE TOKEN for this claim (`tasks.lease_epoch`, arc 101 doc 03) — the
   * ownership generation the claim minted. The box carries it verbatim onto every
   * run event it streams back (`FleetRunEvent.lease_epoch`), and the mothership
   * requires it to match on the TERMINAL task write. A box that kept executing
   * through a partition while the task was re-queued and re-claimed holds a stale
   * epoch, so its `run_finished`/`error` is rejected instead of clobbering the live
   * run — the loser is fenced out at the write, not trusted to notice.
   * Optional ON THE WIRE only because this schema predates the fence — there is no
   * fallback behind it. The box REFUSES an assignment with no epoch: `task-hold.ts`
   * NAKs it, logs `assignment_unfenced` at `alert`, and returns null without running
   * it, because a run whose terminal write nothing can fence is the double-execution
   * the fence exists to stop. Unreachable from a real claim (`tasks.lease_epoch` is
   * `NOT NULL DEFAULT 0`, so `claimNextReady` always returns one) — but that is a
   * property of today's PRODUCER, not the consumer's invariant, which is why the box
   * keeps the check anyway.
   */
  lease_epoch: z.number().int().nonnegative().optional(),
  /**
   * What the task delivers (doc 04). Absent on the wire ⇒ `pr` — a legacy /
   * pre-slice assignment (and the runner's read-site default) still means "open a
   * PR, babysit it". The runner branches on this: `report` / `ops_action` stream a
   * report into run events and SKIP the babysit watch (there is no PR to watch).
   * Optional (not `.default`) so it mirrors `llm_credential` — the wire stays lean
   * and existing assignment fixtures don't all have to carry it.
   */
  outcome_kind: OutcomeKindSchema.optional(),
  llm_credential: SealedLlmCredentialSchema.optional(),
  /**
   * #2203: ACCOUNT-wide fallback pool key the assigner may ship alongside the
   * primary `llm_credential` — the box's review ensemble (#1577/#1651/#1684/
   * #2083) may retry per-sample provider failures against this pool when the
   * primary lane is down (#2203). Same sealed wire shape as the primary — the
   * box cannot unseal it; the assigner writes it in-process after the run's
   * BYOK unwrap. Optional (no `.default()`), mirroring `llm_credential`: a
   * legacy / pre-#2203 assignment with no fallback configured parses
   * byte-identically, and the absence is the assignment's "no fallback pool"
   * signal — same posture as the primary.
   */
  llm_credential_fallback: SealedLlmCredentialSchema.optional(),
  /**
   * The claimed BOX's operator-authored system prompt (#455) — the concise
   * per-runner briefing (`runners.system_prompt`), snapshotted by the assigner at
   * claim time so a `box set_prompt` takes effect on the NEXT assignment with no
   * re-provision. The runner folds it into the fleet prompt composition as its
   * own layer (`composeFleetPrompt({ box })` → `DZ_SYSTEM_PROMPT` for the
   * spawned CLI; in-process brains read it off this assignment). Same cap as the
   * column; optional like `llm_credential` — absent ⇒ no box layer (the wire
   * stays lean, existing fixtures unchanged).
   */
  box_system_prompt: RunnerSystemPromptSchema.optional(),
  /**
   * The claimed BOX's display name (#3878) — `runners.display_name`, snapshotted
   * by the assigner at claim time and cleaned by {@link cleanBoxName} before this
   * parse, so the box can name itself (e.g. in a review footer) without a second
   * channel. Optional: absent ⇒ the box has no name to show and says nothing about
   * which box it is — it must NEVER guess one from `DZ_RUNNER_ID`, because a
   * guessed name is a claim the mothership did not make. A runner built before
   * this field drops the unknown key (plain `z.object` strips), so the rollout
   * needs no ordering.
   */
  box_name: BoxNameSchema.optional(),
  /**
   * The org / repo / user PROSE LAYERS resolved for this task (arc 101 slice 08,
   * epic #635) — the account, repo, and dispatching-member prompts the mothership
   * read (`resolveSystemPromptLayers`) and snapshotted onto the assignment. The box
   * folds each present layer into `composeFleetPrompt` in `platform → org →
   * repo → user` order (`SYSTEM_PROMPT_SCOPE_ORDER`, narrowest-last: a later layer
   * refines an earlier one) — the immutable platform floor is never weakened. Each
   * layer is bounded at `SYSTEM_PROMPT_MAX_CHARS` (the write-boundary guard,
   * re-asserted here). NOT sealed — owner/member prose, same trust class as
   * `box_system_prompt`. Optional like `llm_credential`: absent ⇒ no prose layers
   * (the wire stays lean, existing fixtures unchanged).
   */
  system_prompt_layers: FleetSystemPromptLayersSchema.optional(),
  /**
   * The repo's KNOWLEDGE DIGEST, minted mothership-side at claim time — the lean
   * per-repo index (one line per page: slug + summary), org pages merged under
   * repo pages so the more specific wins.
   *
   * WHY IT RIDES THE ASSIGNMENT. A box cannot reach the database — its NATS
   * credential grants no DB and no HTTP egress to us, and nothing in `apps/runner`
   * imports `@developerz/db` — so it cannot look up what this repo has learned and
   * the digest has to be pushed. (It CAN reach the mothership over
   * `fleet.req.<runnerId>`; that is how it already fetches a git token. A per-page
   * fetch RPC is therefore possible later — this field is the INDEX, not a claim
   * that page bodies are unreachable.) Before this field, the coding lane read
   * `DZ_KB_DIGEST` from the box environment — materialized ONCE at provisioning
   * time from the agent template, with no refresh path. So everything the KB
   * gardener consolidated nightly reached the review lane and never reached the
   * boxes that write the code: the mechanics were tuning a car the drivers could
   * not get into.
   *
   * A SNAPSHOT, exactly like `system_prompt_layers` beside it: resolved inside the
   * claim transaction, so a page written after this claim reaches the NEXT
   * assignment rather than mutating a run in flight. Cleartext — repo memory is
   * owner-authored, same trust class as the prose layers, never sealed.
   *
   * Optional: absent ⇒ the repo has no pages, or the read degraded. A digest is
   * not load-bearing for a run to succeed, so its resolver fails toward shipping
   * the task.
   */
  kb_digest: z.string().min(1).max(KB_DIGEST_MAX_CHARS).optional(),
  /**
   * The prior turns of this envelope's `conversation_ref`, rendered mothership-side
   * at claim time (plan 101 doc 06 step 3) — what makes "talk to the scout about
   * project-1 tomorrow" answerable when the mailbox that carried yesterday's turns
   * closed with yesterday's run.
   *
   * A SNAPSHOT beside `kb_digest`, resolved the same way and for the same reason:
   * the box cannot reach the database, so what is not pushed is not seen. Claim
   * time, not mint time, so a turn spoken between `plan_start` and the claim is
   * still recalled. Rendered by {@link renderConversationDigest} from
   * `human_message` / `agent_response` run events; CLEARTEXT — the customer's own
   * conversation in their own account, same trust class as the prose layers.
   *
   * DELIBERATELY NOT ON THE ENVELOPE, and this is the one-way door of the whole
   * feature. `tasks.envelope` is never pruned; run-event payloads are NULLed at
   * {@link RAW_LOG_RETENTION_HOURS}. Writing recalled turns into the envelope
   * would silently convert a 72-hour retention promise into a permanent second
   * copy of every conversation, in a column no sweep touches. The assignment is a
   * transport (a NATS push and an `outbox` row the retention prune clears), so the
   * source stays the only durable home and the promise stays the source's.
   *
   * Optional like `llm_credential`: absent ⇒ no `conversation_ref`, no prior
   * turns, or a lane {@link ORIGIN_READS_CONVERSATION_DIGEST} says does not read
   * one. The `CONVERSATION_DIGEST_MAX_CHARS` cap is re-asserted here as a backstop.
   */
  conversation_digest: z.string().min(1).max(CONVERSATION_DIGEST_MAX_CHARS).optional(),
  /**
   * THE CARRY-OVER: what the earlier runs of THIS task left on record — how each ended,
   * what it last said, the blockers it raised with the human's answers, the operator
   * messages it was sent and the ones it closed on unread, the files it wrote. Rendered
   * mothership-side at claim time (`apps/worker/src/dispatch/carry-over-resolver.ts`) and
   * read by every lane's brief (`apps/runner/src/carry-over.ts`), screened at run start
   * with the task's own text (`task_carry_over`).
   *
   * WHY IT HAS TO RIDE THE ASSIGNMENT. The runner's per-task ledger is BOX-LOCAL and the
   * pruner deletes it on the way out of the run that would need it; 65% of reassignments
   * land on a different box, which has none (measured 2026-08-19..09-02). A box cannot
   * read Postgres, so what is not pushed here is re-discovered by failing the same way:
   * `tsk_2c6babca2c744dec8cb4170ad612fdf6` ran four times on 2026-09-30, each re-running
   * `bin/setup` into the same root-owned socket dir.
   *
   * It replaced `prior_reason` (#3045), which carried the last terminal reason alone and
   * only the team coordinator read; the terminal reason is the first line of each run here.
   *
   * DELIBERATELY NOT ON THE ENVELOPE, for the reason {@link conversation_digest} states:
   * `tasks.envelope` is never pruned while run-event payloads are NULLed at
   * {@link RAW_LOG_RETENTION_HOURS}, so an envelope-resident copy would make a 72-hour
   * retention promise permanent. The assignment is a transport; the events stay the source.
   *
   * Optional: absent ⇒ a first run, a lane `ORIGIN_READS_CARRY_OVER` says renders none, or
   * a degraded read. Every brief then renders byte-identically to a first run.
   */
  carry_over: z.string().min(1).max(CARRY_OVER_DIGEST_MAX_CHARS).optional(),
  /**
   * Present ONLY for an APPROVED production `ops_action` (doc 04). The box refuses
   * a production ops action that carries no approval or a stale one; this is the
   * proof, minted mothership-side (no assigner writes the field today, and the
   * signed-link source in the wire vocabulary has no mint flow any more). Absent ⇒
   * the box fails closed on a production ops_action.
   */
  ops_approval: OpsApprovalSchema.optional(),
  /**
   * The task repo's sealed secrets (#292, widened by arc 101 slice 06) — the ONE
   * transport for every credential class. Each entry's `blob` is base64 of the
   * KEK-wrapped `credential_vault` / `repo_secrets` ciphertext: the assigner never
   * decrypts; the box unwraps with its KEK at repo-prep (`prepareRepo`: clone via
   * `git`, materialize `app_env` / `repo_env`).
   *
   * Two sources, one array. A `credential`-ingress repo (no GitHub App; the box
   * carries the maintainer's own creds) contributes at most one entry per
   * `credential_vault` class; ANY repo — including the default `app`-ingress SaaS
   * path — contributes one `repo_env` entry per `repo_secrets` row matching this
   * assignment's repo AND environment (`ingressAllowsVaultClass`), hence the
   * `VAULT_MAX_ENTRIES` cap rather than one-per-class. Optional like
   * `llm_credential` — absent ⇒ a repo storing nothing, and the wire stays lean.
   */
  vault: z.array(SealedVaultEntrySchema).max(VAULT_MAX_ENTRIES).optional(),
  /**
   * {@link FleetModelLimitsSchema} for `llm_credential.model` — present exactly when
   * `llm_credential` is. Optional on the wire: an old mothership sends none, and the
   * box then resolves `modelLimits` itself from the credential's provider + model.
   */
  model_limits: FleetModelLimitsSchema.optional(),
  /**
   * How many model streams this run may hold on `llm_credential`'s key AT ONCE — the
   * width capacity-aware admission granted it (`admitOnKey`, `@developerz/domain`) and
   * the units its in-flight claim counts. The box caps its editor fan-out, its review
   * samples and reviewer waves, and its sub-scouts at this number (never above its own
   * box knobs). Present only when the key has a known capacity: absent ⇒ an unbounded
   * (metered) key, and the box keeps its own widths exactly as before. An old box
   * strips the unknown key (this object is not `.strict()`).
   */
  stream_budget: z.number().int().min(1).optional(),
  /**
   * The thinking effort the task's purpose ROUTE pins (plan 101 slice 17) — see
   * {@link FleetReasoningEffortSchema}. Optional and omitted when unset: absent ⇒ the
   * box sends byte-identical requests to the pre-slice-17 ones, and an old box strips
   * the unknown key (this object is not `.strict()`).
   */
  reasoning_effort: FleetReasoningEffortSchema.optional(),
  /**
   * The task's resolved AGENT MODE (plan 101 slice 06) — key, revision, where it came
   * from (repo > org > default), the `code.*` allowlist (`null` = the mode's full
   * ceiling) and any stored names dropped because they left the code. Resolved at
   * claim by a reader that THROWS on failure (rolling the claim back), never one that
   * degrades to shipping nothing — an absent narrowing means the full tool set.
   *
   * Optional ON THE WIRE for the rollout only: this object is not `.strict()`, so a
   * box built before this field strips it and runs un-narrowed. The box echoes what it
   * applied on `prompt_composed.agent_mode`, which is how the audit tells the two
   * apart. A disabled mode never reaches here — it is held (`mode_disabled`).
   */
  agent_mode: AgentModeAssignmentSchema.optional(),
  /**
   * The GitHub identity of the HUMAN behind this task — who dispatched it, started
   * its plan or filed its ask (`findTaskCoAuthor`, `@developerz/db`) — so the box can
   * credit them with a `Co-authored-by:` trailer on the commit and the PR body
   * (`./co-author.ts`). Sent only for a lane in `ORIGIN_READS_CO_AUTHOR`, and only
   * when that person has a linked GitHub account. Absent ⇒ no trailer; an old box
   * strips the unknown key (this object is not `.strict()`).
   */
  co_author: FleetCoAuthorSchema.optional(),
  /**
   * THIS CLAIM WAS ADMITTED FOR PLANNING (#5370): the task's declared area was NOT locked
   * for it, because it had never been planned and this box speaks `files_plan`
   * (`RunnerCapability.PlanAdmission`, derived from its build). The box runs its
   * read-only survey, and its editors write ONLY after `files_plan` answers `locked`;
   * any other answer, or a lane that takes no plan lock, gives the claim back
   * (`run_released`, `plan_lock_unavailable`). Sent only as `true`; absent on every
   * other claim. A box below the cutoff never receives it (the claim filters on the
   * token), and would strip it (this object is not `.strict()`).
   */
  plan_admitted: z.literal(true).optional(),
  /**
   * The PLANNER HAND-OFF flag (#7569, epic #6552): stamped on a scout assignment whose box
   * is to hand a doable ask off (`scout_doable`) instead of planning it in-session. On the
   * ASSIGNMENT, not the envelope, because scout envelopes have several writers. `true` or
   * absent, never `false`. The schema is not `.strict()`, so an older box strips the key.
   * Reader-first: nothing sets it in this release; #6764 is the first writer.
   */
  planner_handoff: z.literal(true).optional(),
});
export type FleetTaskAssignment = z.infer<typeof FleetTaskAssignmentSchema>;

/**
 * Wire contract for a cancel control message on `fleet.rpc.<runner_id>` (#117 /
 * ADR 0001) — core NATS, fire-and-forget. Every publisher, by name:
 *
 *   - the presence reaper (`apps/worker/src/wiring/mothership.ts`, reason
 *     `runner_reaped`), when it resets a lapsed runner's in-flight task to `pending`;
 *   - the operator doors, all through `publishCancel`
 *     (`apps/api/src/deps/fleet-signals.ts`): box drain (`operator_drain`, in
 *     `apps/api/src/deps/box-drain.ts`), and task reassign / cancel / redispatch, plan
 *     cancel and review rerun
 *     (`operator_reassign`, `operator_cancel`, `operator_redispatch`, `review_rerun`, in
 *     `apps/api/src/deps/mcp-ports.ts`);
 *   - the settled-PR retire (`apps/worker/src/babysit/retire-settled-pr.ts`, reason
 *     `pr_settled`, #4239), written to the outbox in the retire's own transaction and
 *     published by the relay (`apps/worker/src/dispatch/relay.ts`).
 *
 * If the runner was only partitioned (not dead) and later reconnects, the cancel
 * tells it to abort the now-revoked task so two boxes never drive the same work.
 * Best-effort: the authoritative fence is the DB row; this is the courtesy signal to
 * a still-connected box, and the box's claim watch (`apps/runner/src/claim-watch.ts`)
 * is the backstop when the message is missed. The `fleet.rpc.<id>` subject already carries the
 * runner id, so the payload need not repeat it; `type` discriminates future
 * control verbs (drain, …) sharing the channel. snake_case — crosses NATS
 * verbatim, same convention as `FleetTaskAssignment`.
 */
export const FleetTaskCancelSchema = z.object({
  type: z.literal('cancel'),
  task_id: TaskIdSchema,
  /** Why the task was pulled — audit + operator legibility (e.g. `runner_reaped`). */
  reason: z.string().min(1),
  /**
   * The lease generation this cancel was minted for (101 04-7) — the same fence the
   * assignment, `whoami_claims`, run events and `llm_credential_next` carry. The box
   * (`apps/runner/src/cancel.ts` `CancelBus.deliver`) aborts nothing when it is LOWER
   * than the held run's epoch: the task was re-queued and re-claimed since, and the
   * cancel is for a generation that no longer runs. Equal or higher aborts.
   *
   * Optional because the publishers above and the fleet's boxes roll separately: an
   * old box strips it (this object is not `.strict()`), and a cancel without it
   * matches on `task_id` alone — the pre-fence behaviour.
   */
  lease_epoch: z.number().int().nonnegative().optional(),
});

/**
 * Pause one run (#6749, epic #6552) — stop it at its next STEP BOUNDARY, save what it can
 * save, and hand the task back as `run_released{reason:'paused'}`. Same channel as the
 * cancel above (`fleet.rpc.<runner_id>`): every enrolled box already holds that grant, and a
 * new subject would need the fleet-wide credential re-issue #2731 measured.
 *
 * `lease_epoch` is REQUIRED, unlike the cancel's: a pause minted for a generation that no
 * longer runs must pause nothing, and "the sender left the fence out" may not be a way to
 * pause the wrong attempt. The box (`apps/runner/src/cancel.ts` `CancelBus.pause`) answers
 * `stale` for a lower epoch than the held run's.
 *
 * THE SENDER IS GATED PER BOX (#6209, #6751). The box's reader shipped first (#6749); the
 * one sender is the task-pause store (`apps/api/src/deps/task-pause-store.ts`), which writes
 * it to the fleet outbox with its stamp (the worker relay delivers it), and only after the
 * task-pause control (`apps/api/src/deps/task-pause.ts`) read the box's OWN live beat listing
 * `pause` in `BoxSlots.verbs` (`scripts/pause-one-sender.test.ts`). Every other box (an older build, a
 * beat with no slots, a value that did not decode) is refused `machine_cannot_pause` and is
 * never sent one; such a box would decode this against a union without it and skip it
 * (`control_decode_failed`).
 */
export const FleetTaskPauseSchema = z.object({
  type: z.literal('pause'),
  task_id: TaskIdSchema,
  lease_epoch: z.number().int().nonnegative(),
});
export type FleetTaskPause = z.infer<typeof FleetTaskPauseSchema>;

/**
 * Mailbox request (#314) — a human message pushed to a LIVE interactive run
 * (scout/setup). Published req/reply over core NATS on
 * `fleet.rpc.msg.<runner_id>.<run_id>` by the control-MCP `message` verb
 * (phone→agent). The run's mailbox injects `text` as a `[Human guidance]`
 * message at the agent's next `prepareStep` and replies on the same subject with
 * the agent's response. A run that is not live holds no channel, so the request
 * finds no responder. snake_case — crosses NATS verbatim, same convention as
 * `FleetTaskAssignment`.
 *
 * THE BOX ID LEADS, AND THAT IS THE SECURITY PROPERTY, not shorthand (#2296).
 * This subject was `fleet.rpc.msg.<run_id>` — one token, no box id — which forced
 * the runner's subscribe grant to `fleet.rpc.msg.>`, and core NATS fans a subject
 * out to every subscriber: one modified box read every tenant's human↔agent
 * guidance, and (holding pub on `_INBOX.>`) could answer first with a forged
 * agent reply. Unguessable run ids never defended it, because a wildcard
 * subscription names no id at all. `@developerz/nats`'s `subjects.mailbox` is the
 * one builder; this docblock is the wire contract a new producer reads, so the
 * two spell the same subject or the old hole comes back through a hand-typed one.
 */
export const FleetMailboxRequestSchema = z.object({
  /** The human's message to the running agent. Bounded so a stray blob can't blow the context. */
  text: z.string().trim().min(1).max(8_000),
  /** Who sent it — audit + agent legibility (e.g. `user:tok_…`). Omitted when anonymous. */
  from: z.string().min(1).max(200).optional(),
});
export type FleetMailboxRequest = z.infer<typeof FleetMailboxRequestSchema>;

/**
 * Mailbox reply (#314) — the agent's response to a {@link FleetMailboxRequest},
 * returned on the same `fleet.rpc.msg.<runner_id>.<run_id>` subject (optionally
 * with a fourth `<agent_id>` token addressing ONE leaf of a hive run — see
 * `subjects.mailbox`). `text` may be empty when
 * the agent acknowledged without prose. A runner that received the guidance but
 * produced no reply within the window answers with the transport's error header
 * instead (delivered, no timely reply), never a forged empty body.
 */
export const FleetMailboxReplySchema = z.object({
  /** The agent's reply text. */
  text: z.string(),
});
export type FleetMailboxReply = z.infer<typeof FleetMailboxReplySchema>;
